/**
 * Depth Tunnel Parallax Effect
 *
 * Creates a subtle parallax effect with three layers:
 * - Background image: Gentle zoom and downward movement
 * - SVG overlay: Opposite movement with slight scaling
 * - Text content: Forward movement with 3D transformations
 *
 * @class DepthTunnelParallax
 */

const HERO_SELECTOR = ".block-heroImage, .block-heroCta";

class DepthTunnelParallax {
	constructor(hero) {
		this.hero = hero;
		this.background = null;
		this.svg = null;
		this.text = null;
		this.isReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		this.init();
	}

	/**
	 * Initialize the parallax effect
	 * Checks for required elements and sets up event listeners
	 */
	init() {
		if (!this.hero || this.isReducedMotion) {
			return;
		}

		this.background = this.hero.querySelector(".hero__parallax-bg");
		this.svg = this.hero.querySelector(".hero__parallax-svg");
		this.text = this.hero.querySelector(".hero__content");

		if (!this.background || !this.svg || !this.text) return;

		this.bindEvents();
		this.updateParallax();
	}

	/**
	 * Bind scroll and resize event listeners
	 * Uses requestAnimationFrame for optimal performance
	 */
	bindEvents() {
		let ticking = false;

		const onScroll = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					this.updateParallax();
					ticking = false;
				});
				ticking = true;
			}
		};

		this.onScroll = onScroll;
		window.addEventListener("scroll", onScroll, { passive: true });

		this.onResize = () => {
			this.updateParallax();
		};
		window.addEventListener("resize", this.onResize);
	}

	/**
	 * Calculate and apply parallax transformations
	 * Based on scroll position relative to hero element
	 */
	updateParallax() {
		const rect = this.hero.getBoundingClientRect();
		const scrollProgress = Math.max(
			0,
			Math.min(1, -rect.top / window.innerHeight),
		);

		if (this.background) {
			const backgroundScale = 1 + scrollProgress * 0.05;
			const backgroundTranslate = scrollProgress * 30;
			const backgroundRotate = scrollProgress * 0.5;

			this.background.style.transform = `
		    translate3d(0, ${backgroundTranslate}px, 0)
		    scale(${backgroundScale})
		    rotate(${backgroundRotate}deg)
		`;
		}

		if (this.svg) {
			const svgTranslate = scrollProgress * -80;
			const svgScale = 1 - scrollProgress * 0.1;
			const svgRotate = scrollProgress * 5;
			const svgTranslateX = scrollProgress * 20;

			this.svg.style.transform = `
        translateY(${svgTranslate}px)
        translateX(${svgTranslateX}px)
        scale(${svgScale})
        rotate(${svgRotate}deg)
      `;
		}

		if (this.text) {
			const isMobile = window.matchMedia("(max-width: 767px)").matches;

			if (isMobile) {
				this.text.style.transform = "none";
				return;
			}

			const textTranslateZ = scrollProgress * 100;
			const textScale = 1 + scrollProgress * 0.1;
			const textTranslateY = scrollProgress * -30;
			const textRotateX = scrollProgress * 2;
			const textRotateY = scrollProgress * 1;

			this.text.style.transform = `
        translateZ(${textTranslateZ}px)
        translateY(${textTranslateY}px)
        scale(${textScale})
        rotateX(${textRotateX}deg)
        rotateY(${textRotateY}deg)
      `;
		}
	}

	/**
	 * Clean up event listeners
	 * Call this when destroying the component
	 */
	destroy() {
		if (this.onScroll) {
			window.removeEventListener("scroll", this.onScroll);
		}
		if (this.onResize) {
			window.removeEventListener("resize", this.onResize);
		}
	}
}

function initHeroParallax() {
	document.querySelectorAll(HERO_SELECTOR).forEach((hero) => {
		new DepthTunnelParallax(hero);
	});
}

export default DepthTunnelParallax;

if (typeof window !== "undefined") {
	document.addEventListener("DOMContentLoaded", () => {
		initHeroParallax();
	});
}
